// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { MathUtils } from '@shared/utils/MathUtils';
import { VolatilityLabBasis } from './VolatilityLabBasis';
import { VolatilityLabChartData } from './VolatilityLabChartData';
import { VolatilityLabSide } from './VolatilityLabSide';
export class OptionVolatilityLab {
    constructor() {
        this._deltaPrecision = 5;
        this._atmStrikeDelta = 50;
        this._callsDeltaValues = [
            95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5
        ];
        this._putsDeltaValues = [
            5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95
        ];
        this._putsCallsDeltaValues = [
            -5, -10, -15, -20, -25, -30, -35, -40, -45, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5
        ];
        this.basis = VolatilityLabBasis.StrikePrices;
        this.side = VolatilityLabSide.CallsAndPuts;
    }
    getVolatilityChartData(inputParameters) {
        const seriesChartData = this.getSeriesChartData(inputParameters);
        let priceStep = inputParameters.underlierPriceStep;
        let pricePrecision = inputParameters.underlierPricePrecision;
        if (this.basis === VolatilityLabBasis.Delta) {
            priceStep = this._deltaPrecision;
            pricePrecision = 0;
        }
        return new VolatilityLabChartData(priceStep, pricePrecision, inputParameters.underlierPrice, seriesChartData.seriesMap, seriesChartData.xScaleLegend);
    }
    getSeriesChartData(inputParameters) {
        const side = this.side;
        const basis = this.basis;
        const seriesMap = new Map();
        const xScaleLegend = new Map();
        for (const entry of inputParameters.seriesMapInputParameters) {
            let delta = 0;
            const deltaValues = new Map();
            if (basis === VolatilityLabBasis.Delta) {
                let deltaArray;
                switch (side) {
                    case VolatilityLabSide.Calls:
                        deltaArray = this._callsDeltaValues;
                        break;
                    case VolatilityLabSide.Puts:
                        deltaArray = this._putsDeltaValues;
                        break;
                    default:
                        deltaArray = this._putsCallsDeltaValues;
                        break;
                }
                for (let i = 0; i < deltaArray.length; i++) {
                    deltaValues.set(deltaArray[i], { str: Math.abs(deltaArray[i]).toString(), values: [] });
                }
            }
            const date = entry[0];
            const minATMStrike = entry[1].minATMStrike;
            const maxATMStrike = entry[1].maxATMStrike;
            const strikesInfo = entry[1].strikesInfo;
            const optionDataSeries = [];
            for (const strikeInfo of strikesInfo) {
                if (side === VolatilityLabSide.Calls && !strikeInfo.callEnabled) {
                    continue;
                }
                if (side === VolatilityLabSide.Puts && !strikeInfo.putEnabled) {
                    continue;
                }
                let iv;
                if (side !== VolatilityLabSide.CallsAndPuts) {
                    const greeks = side === VolatilityLabSide.Calls ? strikeInfo.callGreeks : strikeInfo.putGreeks;
                    iv = greeks.iv * 100;
                    if (basis === VolatilityLabBasis.Delta && isValidNumber(iv)) {
                        delta = Math.abs(100 * greeks.delta);
                    }
                }
                else {
                    if (!isValidNumber(minATMStrike) || !isValidNumber(maxATMStrike)) {
                        continue;
                    }
                    if (strikeInfo.strikePrice > maxATMStrike) {
                        iv = strikeInfo.callGreeks.iv * 100;
                        if (basis === VolatilityLabBasis.Delta && isValidNumber(iv)) {
                            delta = 100 * strikeInfo.callGreeks.delta;
                        }
                    }
                    else if (strikeInfo.strikePrice < minATMStrike) {
                        iv = strikeInfo.putGreeks.iv * 100;
                        if (basis === VolatilityLabBasis.Delta && isValidNumber(iv)) {
                            delta = 100 * strikeInfo.putGreeks.delta;
                        }
                    }
                    else {
                        const ivCall = strikeInfo.callGreeks.iv * 100;
                        const ivPut = strikeInfo.putGreeks.iv * 100;
                        if (basis === VolatilityLabBasis.Delta) {
                            if (isValidNumber(ivCall)) {
                                delta = 100 * strikeInfo.callGreeks.delta;
                                delta = MathUtils.RoundToIncrement(delta, this._deltaPrecision);
                                if (delta > this._atmStrikeDelta || Math.abs(delta) < this._deltaPrecision) {
                                    continue;
                                }
                                const deltaValue = deltaValues.get(delta);
                                if (!isNullOrUndefined(deltaValue)) {
                                    deltaValue.values.push(ivCall);
                                }
                            }
                            if (isValidNumber(ivPut)) {
                                delta = 100 * strikeInfo.putGreeks.delta;
                                if (Math.abs(delta) > this._atmStrikeDelta) {
                                    continue;
                                }
                            }
                        }
                        if (isValidNumber(ivCall) && isValidNumber(ivPut)) {
                            iv = (ivCall + ivPut) / 2;
                        }
                    }
                }
                if (!isValidNumber(iv)) {
                    continue;
                }
                if (basis === VolatilityLabBasis.Delta) {
                    delta = MathUtils.RoundToIncrement(delta, this._deltaPrecision);
                    if (Math.abs(delta) < this._deltaPrecision) {
                        continue;
                    }
                    if (side === VolatilityLabSide.CallsAndPuts && delta === -this._atmStrikeDelta) {
                        delta = Math.abs(delta);
                    }
                    const deltaValue = deltaValues.get(delta);
                    if (!isNullOrUndefined(deltaValue)) {
                        deltaValue.values.push(iv);
                    }
                }
                else {
                    const prevItem = optionDataSeries.at(-1);
                    if (!isNullOrUndefined(prevItem) && prevItem.x === strikeInfo.strikePrice) {
                        prevItem.y = Math.max(prevItem.y, iv);
                    }
                    else {
                        optionDataSeries.push({ x: strikeInfo.strikePrice, y: iv });
                    }
                }
            }
            if (basis === VolatilityLabBasis.Delta) {
                const keys = Array.from(deltaValues.keys());
                for (let i = 0; i < keys.length; i++) {
                    const deltaValue = deltaValues.get(keys[i]);
                    const xValue = (i + 1) * this._deltaPrecision;
                    xScaleLegend.set(xValue, deltaValue.str);
                    if (!isValidArray(deltaValue.values)) {
                        continue;
                    }
                    const yValue = deltaValue.values.reduce((acc, curr) => acc + curr, 0) / deltaValue.values.length;
                    optionDataSeries.push({ x: xValue, y: yValue });
                }
            }
            if (isValidArray(optionDataSeries)) {
                seriesMap.set(date, optionDataSeries);
            }
        }
        return {
            seriesMap,
            xScaleLegend
        };
    }
}
